import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BookUp } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { promotePieceToCorpus, type ArchivedPiece } from "@/lib/composerArchive";
import {
  BUCKET_LABELS,
  BUCKET_ORDER,
  listFolders,
  type CorpusBucket,
  type CorpusFolder,
} from "@/lib/workspaceCorpus";

type Props = {
  piece: ArchivedPiece;
  workspaceId: string;
  draggable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  onClick?: () => void;
};

export const ArchivedRow = ({
  piece,
  workspaceId,
  draggable,
  selected,
  onToggleSelect,
  onDragStart,
  onDragEnd,
  onClick,
}: Props) => {
  const [promoting, setPromoting] = useState(false);
  const corpusFoldersQuery = useQuery({
    queryKey: ["workspace-corpus-folders", workspaceId],
    queryFn: () => listFolders(workspaceId),
    enabled: !!workspaceId,
    staleTime: 60_000,
  });
  const foldersByBucket = useMemo(() => {
    const map: Record<CorpusBucket, CorpusFolder[]> = {
      primary: [],
      secondary: [],
      supplemental: [],
    };
    for (const f of corpusFoldersQuery.data ?? []) {
      if (!f.parent_folder_id) map[f.bucket].push(f);
    }
    return map;
  }, [corpusFoldersQuery.data]);

  const promote = async (bucket: CorpusBucket, targetFolderId: string | null = null) => {
    setPromoting(true);
    try {
      await promotePieceToCorpus({ workspaceId, pieceId: piece.id, bucket, targetFolderId });
      const folderName = targetFolderId
        ? (corpusFoldersQuery.data ?? []).find((f) => f.id === targetFolderId)?.name ?? "folder"
        : "From Archive";
      toast.success(`Promoted to ${BUCKET_LABELS[bucket]} → "${folderName}"`, {
        description: "Drag it to any folder in your Corpus.",
      });
    } catch (e) {
      toast.error((e as Error).message ?? "Promote failed");
    } finally {
      setPromoting(false);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      className={`group flex w-full flex-col gap-1 rounded border bg-card p-2 text-left text-xs transition-colors hover:border-primary/40 hover:bg-muted/30 ${
        selected ? "border-primary/60 bg-primary/5" : "border-border"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {onToggleSelect && (
            <span onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={!!selected}
                onCheckedChange={() => onToggleSelect()}
                aria-label="Select piece"
              />
            </span>
          )}
          <span className="truncate text-sm font-medium">{piece.title || "Untitled"}</span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Badge variant="outline" className="text-[10px] capitalize">
            {piece.content_type ?? "piece"}
          </Badge>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 opacity-0 group-hover:opacity-100"
                disabled={promoting}
                title="Promote to Corpus"
                aria-label="Promote to Corpus"
              >
                <BookUp className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Promote to Corpus
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {BUCKET_ORDER.map((b) => {
                const bucketFolders = foldersByBucket[b];
                if (bucketFolders.length === 0) {
                  return (
                    <DropdownMenuItem key={b} onClick={() => promote(b, null)}>
                      {BUCKET_LABELS[b]} → From Archive
                    </DropdownMenuItem>
                  );
                }
                return (
                  <DropdownMenuSub key={b}>
                    <DropdownMenuSubTrigger>{BUCKET_LABELS[b]}…</DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="max-h-72 overflow-auto">
                      <DropdownMenuItem onClick={() => promote(b, null)}>
                        From Archive (default)
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {bucketFolders.map((f) => (
                        <DropdownMenuItem key={f.id} onClick={() => promote(b, f.id)}>
                          {f.name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1 text-[10px] text-muted-foreground">
        <span>
          {piece.publication_count} publication{piece.publication_count === 1 ? "" : "s"}
        </span>
        {piece.first_published_at ? (
          <span>· first {new Date(piece.first_published_at).toLocaleDateString()}</span>
        ) : null}
        {piece.destination_formats.slice(0, 4).map((d) => (
          <Badge key={d} variant="secondary" className="h-4 px-1 text-[9px]">
            {d}
          </Badge>
        ))}
      </div>
    </div>
  );
};
